import { DEFAULT_ACTIVITY_MINUTES, formatTime } from '@erp/shared';
import { useQueryClient } from '@tanstack/react-query';
import { Lock, X } from 'lucide-react';
import { useState } from 'react';

import { api, ApiError, errorMessage } from '../../lib/api.ts';
import {
  REMINDER_OPTIONS,
  refreshActivities,
  todayIn,
  useActivityTypes,
  useConflicts,
  zonedParts,
  zonedToIso,
  type ActivityItem,
  type NextActivitySuggestion,
} from '../../lib/activities-api.ts';
import { useAssignees, useContacts } from '../../lib/contacts-api.ts';
import { useDeals } from '../../lib/deals-api.ts';
import { useProperties } from '../../lib/properties-api.ts';
import { useSession } from '../../lib/session.ts';
import { useToast } from '../../lib/toast.tsx';
import { Alert } from '../ui/alert.tsx';
import { Button } from '../ui/button.tsx';
import { Combobox } from '../ui/combobox.tsx';
import { Dialog } from '../ui/dialog.tsx';
import { Field, Input, Select } from '../ui/field.tsx';

const textarea = 'w-full rounded-lg border border-border bg-surface p-3 text-base sm:text-sm';

export interface LinkValue {
  id: string;
  label: string;
}

/** What a new activity starts with (from a deal, a contact, a suggestion...). */
export interface ActivityDraft {
  typeId?: string | null;
  /** Picks the first type of this kind when there is no typeId. */
  kind?: string;
  title?: string | null;
  date?: string;
  time?: string;
  deal?: LinkValue | null;
  contact?: LinkValue | null;
  property?: LinkValue | null;
  /** "Registrar": it already happened; it is created and completed at once. */
  logDone?: boolean;
}

/**
 * Create or edit an activity. Private: personal, for oneself, with no links. Overlaps
 * with other activities of the responsible are shown as a warning; saving is allowed.
 */
export function ActivityDialog({
  activity,
  draft,
  onClose,
  onSaved,
}: {
  activity?: ActivityItem;
  draft?: ActivityDraft;
  onClose: () => void;
  onSaved?: (saved: ActivityItem, suggestion: NextActivitySuggestion | null) => void;
}) {
  const { data: session } = useSession();
  const tz = session?.tenant.timezone ?? 'America/El_Salvador';
  const me = session?.membership.id ?? '';
  const types = useActivityTypes();
  const assignees = useAssignees();
  const client = useQueryClient();
  const notify = useToast();

  const now = zonedParts(new Date(), tz);
  const start = activity ? zonedParts(activity.startsAt, tz) : null;
  const end = activity?.endsAt ? zonedParts(activity.endsAt, tz) : null;
  const initialType =
    activity?.type.id ??
    draft?.typeId ??
    types.data?.find((t) => t.kind === draft?.kind)?.id ??
    types.data?.[0]?.id ??
    '';
  const [chosenType, setTypeId] = useState('');
  // The types may arrive after the dialog opens.
  const typeId = chosenType || initialType;
  const [title, setTitle] = useState(activity?.title ?? draft?.title ?? '');
  const [date, setDate] = useState(start?.date ?? draft?.date ?? todayIn(tz));
  const [time, setTime] = useState(start?.time ?? draft?.time ?? nextHalfHour(now.time));
  const [endTime, setEndTime] = useState(end?.time ?? '');
  const [allDay, setAllDay] = useState(activity?.allDay ?? false);
  const [isPrivate, setPrivate] = useState(activity?.isPrivate ?? false);
  const [assignee, setAssignee] = useState(activity?.assignedTo.membershipId ?? me);
  const [deal, setDeal] = useState<LinkValue | null>(
    activity?.deal ? { id: activity.deal.id, label: activity.deal.title } : (draft?.deal ?? null),
  );
  const [contact, setContact] = useState<LinkValue | null>(
    activity?.contact
      ? { id: activity.contact.id, label: activity.contact.name ?? 'Contacto de otra cartera' }
      : (draft?.contact ?? null),
  );
  const [property, setProperty] = useState<LinkValue | null>(
    activity?.property
      ? {
          id: activity.property.id,
          label: `${activity.property.code} · ${activity.property.title}`,
        }
      : (draft?.property ?? null),
  );
  const [note, setNote] = useState(activity?.note ?? '');
  const [reminder, setReminder] = useState<string>(
    activity ? String(activity.reminderMinutes ?? 'none') : 'default',
  );
  const [resultId, setResultId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const type = types.data?.find((t) => t.id === typeId);
  const logDone = Boolean(draft?.logDone) && !activity;
  const startsAt = date && (allDay || time) ? zonedToIso(date, allDay ? '00:00' : time, tz) : null;
  const endsAt =
    !allDay && date && endTime && endTime > time ? zonedToIso(date, endTime, tz) : null;
  const conflicts = useConflicts(
    startsAt && !allDay && !logDone
      ? {
          startsAt,
          endsAt: endsAt ?? undefined,
          assignedMembershipId: isPrivate ? me : assignee,
          excludeId: activity?.id,
        }
      : null,
  );

  const save = async () => {
    if (!startsAt || !typeId) {
      setError('Elige el tipo, la fecha y la hora.');
      return;
    }
    setSaving(true);
    setError(null);
    const body = {
      typeId,
      title: title.trim() || null,
      startsAt,
      endsAt,
      allDay,
      isPrivate,
      assignedMembershipId: isPrivate ? me : assignee,
      dealId: isPrivate ? null : (deal?.id ?? null),
      contactId: isPrivate ? null : (contact?.id ?? null),
      propertyId: isPrivate ? null : (property?.id ?? null),
      note: note.trim() || null,
      ...(reminder === 'default'
        ? {}
        : { reminderMinutes: reminder === 'none' ? null : Number(reminder) }),
    };
    try {
      let saved = activity
        ? await api<ActivityItem>(`/activities/${activity.id}`, { method: 'PATCH', body })
        : await api<ActivityItem>('/activities', { method: 'POST', body });
      let suggestion: NextActivitySuggestion | null = null;
      if (logDone) {
        const done = await api<{
          activity: ActivityItem;
          suggestion: NextActivitySuggestion | null;
        }>(`/activities/${saved.id}/complete`, {
          method: 'POST',
          body: { resultId: resultId || null },
        });
        saved = done.activity;
        suggestion = done.suggestion;
      }
      await refreshActivities(client);
      notify(
        logDone ? 'Actividad registrada.' : activity ? 'Actividad guardada.' : 'Actividad creada.',
        'success',
      );
      // Closing first: onSaved may open the next dialog (a suggestion).
      onClose();
      onSaved?.(saved, suggestion);
    } catch (e) {
      setError(
        e instanceof ApiError && e.issues.length > 0
          ? (e.issues[0]?.message ?? errorMessage(e))
          : errorMessage(e),
      );
    } finally {
      setSaving(false);
    }
  };

  const others = (assignees.data ?? []).filter((a) => a.membershipId !== me);

  return (
    <Dialog
      open
      onClose={onClose}
      title={
        activity
          ? 'Editar actividad'
          : logDone
            ? `Registrar ${type?.name.toLowerCase() ?? 'actividad'}`
            : 'Nueva actividad'
      }
    >
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Tipo">
            {(a11y) => (
              <Select
                {...a11y}
                value={typeId}
                disabled={Boolean(activity?.result)}
                onChange={(e) => {
                  setTypeId(e.target.value);
                  setResultId('');
                }}
              >
                {(types.data ?? []).map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Título" hint="Si lo dejas vacío, se arma con el tipo y el cliente.">
            {(a11y) => (
              <Input
                {...a11y}
                maxLength={150}
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);
                }}
              />
            )}
          </Field>
        </div>

        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Fecha">
            {(a11y) => (
              <Input
                {...a11y}
                type="date"
                required
                value={date}
                onChange={(e) => {
                  setDate(e.target.value);
                }}
              />
            )}
          </Field>
          {!allDay && (
            <>
              <Field label="Hora">
                {(a11y) => (
                  <Input
                    {...a11y}
                    type="time"
                    required
                    step={300}
                    value={time}
                    onChange={(e) => {
                      setTime(e.target.value);
                    }}
                  />
                )}
              </Field>
              <Field
                label="Termina"
                hint={`Sin hora: ${String(DEFAULT_ACTIVITY_MINUTES)} minutos.`}
              >
                {(a11y) => (
                  <Input
                    {...a11y}
                    type="time"
                    step={300}
                    value={endTime}
                    onChange={(e) => {
                      setEndTime(e.target.value);
                    }}
                  />
                )}
              </Field>
            </>
          )}
        </div>
        <label className="flex min-h-11 items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="size-5"
            checked={allDay}
            onChange={(e) => {
              setAllDay(e.target.checked);
            }}
          />
          Todo el día
        </label>

        {conflicts.data && conflicts.data.length > 0 && (
          <Alert tone="warning" title="Se cruza con otra actividad">
            <ul className="space-y-0.5">
              {conflicts.data.map((c, i) => (
                <li key={c.id ?? `busy-${String(i)}`}>
                  {formatTime(c.startsAt, tz)}
                  {c.endsAt ? `–${formatTime(c.endsAt, tz)}` : ''} · {c.title}
                </li>
              ))}
            </ul>
            <p className="mt-1">Puedes guardarla igual.</p>
          </Alert>
        )}

        {!logDone && (
          <label className="flex min-h-11 items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-0.5 size-5"
              checked={isPrivate}
              disabled={Boolean(activity) && activity?.assignedTo.membershipId !== me}
              onChange={(e) => {
                setPrivate(e.target.checked);
              }}
            />
            <span>
              <span className="flex items-center gap-1 font-medium">
                <Lock className="size-4" aria-hidden="true" /> Privada
              </span>
              <span className="text-muted-foreground">
                Personal: solo tú ves el detalle; los demás ven “Ocupado”. No se vincula a clientes,
                negocios ni propiedades.
              </span>
            </span>
          </label>
        )}

        {!isPrivate && (
          <div className="grid gap-3 sm:grid-cols-2">
            {others.length > 0 && (
              <Field label="Responsable">
                {(a11y) => (
                  <Select
                    {...a11y}
                    value={assignee}
                    onChange={(e) => {
                      setAssignee(e.target.value);
                    }}
                  >
                    <option value={me}>Yo</option>
                    {others.map((a) => (
                      <option key={a.membershipId} value={a.membershipId}>
                        {a.name}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
            )}
            <DealPicker value={deal} onChange={setDeal} />
            <ContactPicker value={contact} onChange={setContact} />
            <PropertyPicker value={property} onChange={setProperty} />
          </div>
        )}

        {logDone && type && type.results.length > 0 && (
          <Field label="Resultado">
            {(a11y) => (
              <Select
                {...a11y}
                value={resultId}
                onChange={(e) => {
                  setResultId(e.target.value);
                }}
              >
                <option value="">Sin resultado</option>
                {type.results.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        )}

        <Field label="Nota">
          {(a11y) => (
            <textarea
              {...a11y}
              rows={3}
              maxLength={5000}
              className={textarea}
              value={note}
              onChange={(e) => {
                setNote(e.target.value);
              }}
            />
          )}
        </Field>

        {!logDone && (
          <Field label="Recordatorio">
            {(a11y) => (
              <Select
                {...a11y}
                value={reminder}
                onChange={(e) => {
                  setReminder(e.target.value);
                }}
              >
                {!activity && (
                  <option value="default">
                    Según el tipo
                    {type?.defaultReminderMinutes != null && !allDay
                      ? ` (${String(type.defaultReminderMinutes)} min antes)`
                      : ''}
                  </option>
                )}
                {REMINDER_OPTIONS.map(([value, label]) => (
                  <option key={label} value={value === null ? 'none' : String(value)}>
                    {label}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        )}

        {error && <Alert tone="danger">{error}</Alert>}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" variant="brand" loading={saving}>
            {logDone ? 'Registrar' : 'Guardar'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

/** 09:10 → 09:30; 09:40 → 10:00. */
function nextHalfHour(time: string): string {
  const [h = 0, m = 0] = time.split(':').map(Number);
  const total = Math.min(h * 60 + m + (30 - (m % 30)), 23 * 60 + 30);
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

function Chosen({
  label,
  value,
  onClear,
}: {
  label: string;
  value: LinkValue;
  onClear: () => void;
}) {
  return (
    <div className="space-y-1.5">
      <p className="text-sm font-medium">{label}</p>
      <div className="flex min-h-11 items-center justify-between gap-2 rounded-lg border border-border px-3 text-sm">
        <span className="truncate">{value.label}</span>
        <Button
          size="icon"
          variant="ghost"
          aria-label={`Quitar ${label.toLowerCase()}`}
          onClick={onClear}
        >
          <X />
        </Button>
      </div>
    </div>
  );
}

function DealPicker({
  value,
  onChange,
}: {
  value: LinkValue | null;
  onChange: (value: LinkValue | null) => void;
}) {
  const [query, setQuery] = useState('');
  const results = useDeals({ q: query.trim(), status: 'open' }, query.trim().length > 0);
  if (value)
    return (
      <Chosen
        label="Negocio"
        value={value}
        onClear={() => {
          onChange(null);
        }}
      />
    );
  return (
    <Field label="Negocio">
      {(a11y) => (
        <Combobox
          id={a11y.id}
          describedBy={a11y['aria-describedby']}
          query={query}
          onQueryChange={setQuery}
          loading={results.isFetching}
          placeholder="Busca por título o cliente"
          options={(results.data?.items ?? []).map((d) => ({
            id: d.id,
            label: d.title,
            detail: `${d.contact.name} · ${d.stage.name}`,
          }))}
          onSelect={(o) => {
            onChange({ id: o.id, label: o.label });
          }}
        />
      )}
    </Field>
  );
}

function ContactPicker({
  value,
  onChange,
}: {
  value: LinkValue | null;
  onChange: (value: LinkValue | null) => void;
}) {
  const [query, setQuery] = useState('');
  const results = useContacts({ q: query.trim() }, query.trim().length > 0);
  if (value)
    return (
      <Chosen
        label="Contacto"
        value={value}
        onClear={() => {
          onChange(null);
        }}
      />
    );
  return (
    <Field label="Contacto" hint="Con un negocio, se usa su cliente.">
      {(a11y) => (
        <Combobox
          id={a11y.id}
          describedBy={a11y['aria-describedby']}
          query={query}
          onQueryChange={setQuery}
          loading={results.isFetching}
          placeholder="Nombre, teléfono o correo"
          options={(results.data?.items ?? []).map((c) => ({ id: c.id, label: c.name }))}
          onSelect={(o) => {
            onChange({ id: o.id, label: o.label });
          }}
        />
      )}
    </Field>
  );
}

function PropertyPicker({
  value,
  onChange,
}: {
  value: LinkValue | null;
  onChange: (value: LinkValue | null) => void;
}) {
  const [query, setQuery] = useState('');
  const results = useProperties({ q: query.trim() }, query.trim().length > 0);
  if (value)
    return (
      <Chosen
        label="Propiedad"
        value={value}
        onClear={() => {
          onChange(null);
        }}
      />
    );
  return (
    <Field label="Propiedad">
      {(a11y) => (
        <Combobox
          id={a11y.id}
          describedBy={a11y['aria-describedby']}
          query={query}
          onQueryChange={setQuery}
          loading={results.isFetching}
          placeholder="Código o título"
          options={(results.data?.items ?? []).map((p) => ({
            id: p.id,
            label: `${p.code} · ${p.title}`,
            detail: p.district.name,
          }))}
          onSelect={(o) => {
            onChange({ id: o.id, label: o.label });
          }}
        />
      )}
    </Field>
  );
}
